import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {IsContainerFixedPlateWellQuery} from 'inventory/sample-status/__generated/IsContainerFixedPlateWellQuery';
import {Party, PartySummary} from 'parties/PartyConstants';
import {Optional} from 'util/Types';

type ParentStorableInfo = NonNullable<
  ExtractByTypename<IsContainerFixedPlateWellQuery['containers'][number], 'ContainerNode'>['parentStorable']
>;

export enum CheckOutStatus {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  CHECKED_OUT = 'CHECKED_OUT',
}

export interface CheckOutStatusInfo {
  assignee?: Party;
  comment: string;
  createdAt: string;
  isAssignedToCurrentUser?: boolean;
  status: CheckOutStatus;
}

export interface MinimalExpirationInfo {
  expirationDate: string | null;
  isExpired: boolean;
  isOverridden?: boolean;
  isTrackingEnabled: boolean;
}

export interface MinimalFreezeThawInfo {
  freezeThawCount: number | null;
  isTrackingEnabled: boolean;
}

// These properties are required for containers & will not be present on any other storable types; by setting
// the types up like this, we can easily handle both cases.
export interface StorableWithValidContentsInfo {
  id: string;
  expirationInfo?: MinimalExpirationInfo;
  freezeThawInfo?: MinimalFreezeThawInfo;
}

export interface SampleStatusItem extends StorableWithValidContentsInfo {
  id: string;
  sortKey?: string;
  archiveRecord?: ArchiveRecord | null;
  checkOutStatusInfo?: CheckOutStatusInfo;
  restrictionStatus?: SampleRestrictionStatus;
  sampleRoles?: Partial<SampleRoles>;
  permissions?: Permissions;
  parentStorable?: ParentStorableInfo | null;
}

export interface SampleStatusItemForValidation extends SampleStatusItem {
  permissions: Permissions;
  parentStorableId?: Optional<string>;
}

export interface CICOItemIdsToAssignee {
  [itemId: string]: PartySummary;
}

export interface CICOActionResponse {
  [itemId: string]: CheckOutStatusInfo;
}

export interface SampleRoleParty {
  id: string;
  name: string;
  handle: string;
  avatarUrl?: string | null;
}

export enum SampleRestrictionStatus {
  RESTRICTED = 'RESTRICTED',
  UNRESTRICTED = 'UNRESTRICTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export interface SampleControlAttributes {
  sampleRoles: {
    sampleOwners: Array<SampleRoleParty>;
    restrictedSampleUsers: Array<SampleRoleParty>;
  };
  restrictionStatus: SampleRestrictionStatus;
}

export interface SampleControlFields {
  sampleOwners: Array<SampleRoleParty>;
  restrictedSampleUsers: Array<SampleRoleParty>;
  restrictionStatus: SampleRestrictionStatus;
}

export interface SampleControlParams {
  restrictionStatus?: SampleRestrictionStatus;
  sampleRoles?: {
    sampleOwnerIds?: Array<string>;
    restrictedSampleUserIds?: Array<string>;
  };
}

// Keep in sync with benchling.inventory.sample_roles.models.mixins.sample_role_mixin.SAMPLE_RESTRICTION_STATUS_LABELS
export const SAMPLE_RESTRICTION_STATUS_LABELS = {
  [SampleRestrictionStatus.RESTRICTED]: 'Restricted',
  [SampleRestrictionStatus.UNRESTRICTED]: 'Unrestricted',
  [SampleRestrictionStatus.NOT_APPLICABLE]: 'N/A',
};

// Keep in sync with benchling.inventory.sample_roles.models.mixins.sample_role_mixin.SampleRestrictionAccess
export enum SampleRestrictionAccess {
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  NO_ACCESS = 'NO_ACCESS',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export const SAMPLE_RESTRICTION_ACCESS_LABELS = {
  [SampleRestrictionAccess.ACCESS_GRANTED]: 'Access granted',
  [SampleRestrictionAccess.NO_ACCESS]: 'No access',
  [SampleRestrictionAccess.NOT_APPLICABLE]: 'N/A',
};

export interface SampleRoles {
  id: string;
  isCurrentUserRestrictedSampleUser: boolean;
  isCurrentUserSampleOwner: boolean;
  sampleOwners: Array<SampleRoleParty>;
  restrictedSampleUsers: Array<SampleRoleParty>;

  // TODO(BNCH-57025) remove after bake time
  isCurrentUserAuthorized: boolean;
  authorizedUsers: Array<SampleRoleParty>;
}

export const EDITING_SAMPLE_OWNERS_DISABLED_MESSAGE = 'You must have admin permissions to edit sample owners';
export const EDITING_SAMPLE_RESTRICTION_STATUS_DISABLED_MESSAGE =
  'You must have permission to restrict or unrestrict samples';

export enum SampleStatusWarningType {
  CICO_WARNING = 'CICO_WARNING',
  SAMPLE_CONTROLS_WARNING = 'SAMPLE_CONTROLS_WARNING',
}
